import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { BookOpen, GraduationCap, HelpCircle, Loader2, Sparkles, ArrowRight, Lock } from "lucide-react";
import { getPublicLecture } from "@/lib/api";
import { LectureView } from "@/components/LectureView";
import { StudyGuide } from "@/components/StudyGuide";
import { FounderBadge } from "@/components/FounderBadge";

export const SharedLecture = () => {
  const { token } = useParams();
  const [lecture, setLecture] = useState(null);
  const [tab, setTab] = useState("lecture");
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    getPublicLecture(token)
      .then(setLecture)
      .catch((e) => setErr(e?.response?.data?.detail || "This share link has been revoked or never existed."));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center">
        <div>
          <h1 className="font-serif text-3xl font-bold" data-testid="share-not-found-heading">Share link not available</h1>
          <p className="mt-2 text-muted-foreground">{err}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold">
            Go to Lectura <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!lecture) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="font-serif text-2xl font-bold tracking-tight leading-none">
              Lectura<span className="text-accent">.</span>
            </span>
            <span className="ml-2 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">Public preview</span>
          </Link>
          <Link
            to="/"
            data-testid="shared-create-cta"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:opacity-90 transition"
          >
            <Sparkles className="h-4 w-4" /> Create your own
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-accent/30 bg-accent/5 px-5 py-4 flex flex-wrap items-center gap-3" data-testid="share-attribution-banner">
          <Sparkles className="h-4 w-4 text-accent shrink-0" />
          <p className="text-sm text-foreground/80">
            <span className="font-semibold">{lecture.author_name}</span>
            {lecture.author_is_founder && <FounderBadge className="ml-2 align-middle" />}
            {" "}shared this study guide with you.
            {" "}Sign up free to unlock the AI narration, full quiz and your own guides.
          </p>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/featured"
              data-testid="share-explore-featured"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
            >
              Explore more guides
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition"
            >
              Try Lectura free <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>

      <main className="relative z-10 pb-24">
        {tab === "lecture" && <LectureView lecture={lecture} readOnly />}
        {tab === "guide" && <StudyGuide lecture={lecture} />}
        {tab === "quiz" && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="rounded-3xl border border-border bg-card p-8 sm:p-10 text-center shadow-sm" data-testid="share-quiz-cta">
              <div className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-accent/15 text-accent">
                <Lock className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-serif text-3xl font-bold">
                Take the full {lecture.quiz_count}-question quiz
              </h2>
              <p className="mt-3 text-muted-foreground">
                The full graded quiz with explanations is one of the perks of a free Lectura account —
                along with AI narration, printable guides and unlimited study material.
              </p>

              {(lecture.quiz_preview || []).length > 0 && (
                <div className="mt-6 text-left space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">A tiny sample</p>
                  {lecture.quiz_preview.slice(0, 2).map((q, i) => (
                    <div key={i} className="rounded-xl border border-border bg-background/60 p-4">
                      <p className="text-sm font-semibold" data-testid={`share-quiz-preview-${i}`}>Q{i + 1}. {q.question}</p>
                      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                        {(q.options || []).map((o, j) => (
                          <li key={j}>{String.fromCharCode(65 + j)}. {o}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <Link
                to="/"
                data-testid="share-quiz-signup-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                <Sparkles className="h-4 w-4" /> Sign up free to unlock the quiz
              </Link>
            </div>
          </div>
        )}
      </main>

      <div className="no-print fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex gap-1 rounded-full border border-border bg-background/90 backdrop-blur-md p-1 shadow-lg">
        {[
          { id: "lecture", label: "Lecture", icon: BookOpen },
          { id: "guide", label: "Guide", icon: GraduationCap },
          { id: "quiz", label: "Quiz", icon: HelpCircle },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`shared-subnav-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};
